import {
  signInAnonymously,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  updateProfile
} from 'firebase/auth';
import { auth } from '../config/firebase';
import firestoreService from './firestoreService';

class AuthService {
  constructor() {
    this.currentUser = null;
    this.authStateListeners = [];
  }

  // Anonymous Authentication
  async signInAnonymously() {
    try {
      const userCredential = await signInAnonymously(auth);
      const user = userCredential.user;
      
      // Create user document in Firestore
      await firestoreService.createUser({
        uid: user.uid,
        email: null,
        displayName: `Anonymous User ${user.uid.substring(0, 6)}`,
        photoURL: null,
        isAnonymous: true
      });
      
      return user;
    } catch (error) {
      console.error('Error signing in anonymously:', error);
      throw error;
    }
  }

  // Sign Out
  async signOut() {
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }

  // Update User Profile
  async updateUserProfile(displayName, photoURL) {
    try {
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, {
          displayName,
          photoURL
        });
        
        // Ensure user document exists before updating
        const existingUser = await firestoreService.getUser(auth.currentUser.uid);
        
        if (!existingUser) {
          // Create user document if it doesn't exist
          await firestoreService.createUser({
            uid: auth.currentUser.uid,
            email: auth.currentUser.email,
            displayName: displayName || auth.currentUser.displayName,
            photoURL: photoURL || auth.currentUser.photoURL,
            isAnonymous: auth.currentUser.isAnonymous
          });
        } else {
          // Update existing user document
          await firestoreService.updateUser(auth.currentUser.uid, {
            displayName,
            photoURL
          });
        }
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  }

  // Get Current User
  getCurrentUser() {
    return auth.currentUser;
  }

  // Auth State Observer
  onAuthStateChange(callback) {
    return onAuthStateChanged(auth, async (user) => {
      this.currentUser = user;
      
      if (user) {
        // Ensure user document exists and update last login time
        try {
          // First check if user document exists
          const existingUser = await firestoreService.getUser(user.uid);
          
          if (!existingUser) {
            // Create user document if it doesn't exist
            await firestoreService.createUser({
              uid: user.uid,
              email: user.email,
              displayName: user.displayName || `Anonymous User ${user.uid.substring(0, 6)}`,
              photoURL: user.photoURL,
              isAnonymous: user.isAnonymous
            });
          } else {
            // Update last login time for existing user
            await firestoreService.updateUser(user.uid, {
              lastLoginAt: new Date()
            });
          }
        } catch (error) {
          console.error('Error handling user document:', error);
        }
      }
      
      callback(user);
    });
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!auth.currentUser;
  }

  // Get user token
  async getUserToken() {
    if (auth.currentUser) {
      return await auth.currentUser.getIdToken();
    }
    return null;
  }
}

const authService = new AuthService();
export default authService;
